const EventEmitter = require('events');
const Logger = require('../Logger').default;
const { timeoutPromise } = require('../utils/timeoutPromise');
// const createTFLiteSIMDModule = require('../utils/tflite/tflite-simd');
// const createTFLiteModule = require('../utils/tflite/tflite');
const logger = new Logger('EffectsService');

class MockTFLite
{
	constructor()
	{
		this.modelBufferMemoryOffset = 0;
		this.inputMemoryOffset = 0;
		this.inputHeight = 256;
		this.inputWidth = 256;
		this.inputChannelCount = 3;
		this.outputMemoryOffset = 0;
		this.outputHeight = 256;
		this.outputWidth = 256;
		this.outputChannelCount = 1;
		this.HEAPU8 = new Uint8Array(1024 * 1024); // Simulated memory space
		this.HEAPF32 = new Float32Array(1024 * 1024); // Simulated memory space
		this._defineFunctionProperties();
	}

	_defineFunctionProperties()
	{
		Object.defineProperties(this,
			{
				_getModelBufferMemoryOffset :
				{
					value        : () => this.modelBufferMemoryOffset,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getInputMemoryOffset :
				{
					value        : () => this.inputMemoryOffset,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getInputHeight :
				{
					value        : () => this.inputHeight,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getInputWidth :
				{
					value        : () => this.inputWidth,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getInputChannelCount :
				{
					value        : () => this.inputChannelCount,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getOutputMemoryOffset :
				{
					value        : () => this.outputMemoryOffset,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getOutputHeight :
				{
					value        : () => this.outputHeight,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getOutputWidth :
				{
					value        : () => this.outputWidth,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_getOutputChannelCount :
				{
					value        : () => this.outputChannelCount,
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_loadModel :
				{
					value : (bufferSize) =>
					{
						this.modelBufferMemoryOffset = 128;

						return this.modelBufferMemoryOffset;
					},
					writable     : false,
					configurable : false,
					enumerable   : false
				},
				_runInference :
				{
					value        : () => 0, // Simulated success
					writable     : false,
					configurable : false,
					enumerable   : false
				}
			});
	}
}

async function createTFLiteModule()
{
	return window.createTFLiteModule();
}

async function createTFLiteSIMDModule()
{
	return window.createTFLiteSIMDModule();
}

export const modelConfig =
{
	path   : '/model/selfie_segmenter_landscape.tflite',
	width  : 256,
	height : 144
};

let BlurredTrack;
let VirtualBackgroundTrack;

export class EffectsService extends EventEmitter
{
	constructor()
	{
		super();
		this.effectTracks = new Map();
		this.model = null;
		// Cached TFLite WASM backend. Instantiating it is expensive (loads a
		// script + compiles WASM), so we build it once and reuse it across every
		// effect switch instead of recreating it each time. The HEAP is shared,
		// so only one effect may run inference at a time — pause the previous
		// track before creating a new one (see RoomClient._switchWebcamEffect).
		this.mlBackend = null;
		// True once model weights have been copied into mlBackend HEAP.
		this.modelLoadedOntoBackend = false;
		this.webGLSupport = this.detectWebGLSupport();
	}

	detectWebGLSupport()
	{
		try
		{
			const canvas = document.createElement('canvas');
			
			// Check for WebGL 2.0 first (preferred)
			const gl2 = canvas.getContext('webgl2');
			if (gl2)
			{
				// Additional check for Safari WebGL 2.0 stability
				const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
				if (isSafari)
				{
					// Safari WebGL 2.0 can be unstable, use WebGL 1.0 fallback
					logger.warn('Safari detected, using WebGL 1.0 fallback for stability');
					return 'webgl1';
				}
				return 'webgl2';
			}
			
			// Fallback to WebGL 1.0
			const gl1 = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
			if (gl1)
			{
				return 'webgl1';
			}
			
			return false;
		}
		catch (error)
		{
			logger.error('WebGL detection failed:', error);
			return false;
		}
	}

	async applyEffect(track, blurPercentage)
	{

		logger.debug('applyEffect() [track.id %s, kind: %s]', track.id, track.kind);

		if (track.kind !== 'video')
		{
			throw new Error('Audio effects are not yet implemented.');
		}

		// Blur uses the proven main-thread WebGL pipeline. The Offscreen worker
		// blur composite was dropping the background blur (looked like no effect);
		// image backgrounds still prefer the worker via applyImageBackground().
		const MLBackend = await this.ensureModelLoaded();

		if (!BlurredTrack)
		{
			({ BlurTrack: BlurredTrack } = await import('../utils/blurbackground/BlurTrack'));
		}

		const effectTrack = new BlurredTrack(MLBackend, this.model, track, this.webGLSupport, blurPercentage);

		this.effectTracks.set(effectTrack.outputTrack.id, effectTrack);

		return effectTrack.outputTrack;
	}

	/**
	 * Pause inference on an effect track without ending its MediaStreamTracks.
	 * Must be awaited before another effect may use the shared TFLite HEAP.
	 */
	async pause(trackId)
	{
		if (!trackId) return;

		logger.debug('pause() [trackId %s]', trackId);

		const track = this.effectTracks.get(trackId);

		if (track?.pause)
		{
			await track.pause();
		}
	}

	resume(trackId)
	{
		if (!trackId) return;

		logger.debug('resume() [trackId %s]', trackId);

		const track = this.effectTracks.get(trackId);

		if (track?.resume)
		{
			track.resume();
		}
	}

	stop(trackId)
	{
		if (!trackId) return;

		logger.debug('stop() [trackId %s]', trackId);

		const track = this.effectTracks.get(trackId);

		if (track)
		{
			track.stop();
			this.effectTracks.delete(trackId);
		}
	}

	async removeEffect()
	{
		logger.debug('removeEffect()');
		
		// Stop all active effect tracks
		for (const [trackId, effectTrack] of this.effectTracks)
		{
			effectTrack.stop();
		}
		this.effectTracks.clear();
	}

	async applyImageBackground(track, imageUrl)
	{
		logger.debug('applyImageBackground() [track.id %s, imageUrl: %s]', track.id, imageUrl);

		if (track.kind !== 'video')
		{
			throw new Error('Audio effects are not yet implemented.');
		}

		// Use ML-based virtual background implementation
		try
		{
			const offscreenTrack = await this._tryOffscreenEffect({
				inputTrack : track,
				mode       : 'image',
				imageUrl
			});

			if (offscreenTrack)
			{
				return offscreenTrack;
			}

			const MLBackend = await this.ensureModelLoaded();

			if (!VirtualBackgroundTrack)
			{
				({ VirtualBackgroundTrack } = await import('../utils/blurbackground/VirtualBackgroundTrack'));
			}

			// Create a virtual background track using the same ML backend as blur
			const effectTrack = new VirtualBackgroundTrack(MLBackend, this.model, track, this.webGLSupport, imageUrl);

			this.effectTracks.set(effectTrack.outputTrack.id, effectTrack);

			return effectTrack.outputTrack;
		}
		catch (error)
		{
			logger.error('applyImageBackground() error:', error);
			// Fallback to original track if virtual background fails
			logger.warn('Virtual background failed, returning original track');
			return track;
		}
	}

	/**
	 * True when trackId is a live effect pipeline we can pause/stop/resume.
	 * Used by RoomClient so a failed image apply (which may return a raw
	 * camera clone) is not mistaken for an active effect.
	 */
	hasEffectTrack(trackId)
	{
		return Boolean(trackId && this.effectTracks.has(trackId));
	}

	/**
	 * Try the OffscreenCanvas worker path. Returns an output MediaStreamTrack
	 * on success, or null to signal the caller should use the main-thread path.
	 *
	 * Disabled by default: the worker path caused production flow breaks
	 * (WASM path 404, missing blur, motion ghosting). Keep the code warm but
	 * prefer the proven main-thread BlurTrack / VirtualBackgroundTrack until
	 * the worker is soak-tested. Flip ENABLE_OFFSCREEN_EFFECTS to re-enable.
	 */
	async _tryOffscreenEffect({ inputTrack, mode, blurPercentage, imageUrl })
	{
		const ENABLE_OFFSCREEN_EFFECTS = false;

		if (!ENABLE_OFFSCREEN_EFFECTS)
		{
			return null;
		}

		try
		{
			const {
				OffscreenEffectTrack,
				supportsOffscreenEffect
			} = await import('../utils/blurbackground/OffscreenEffectTrack');

			if (!supportsOffscreenEffect())
			{
				return null;
			}

			logger.debug('_tryOffscreenEffect() using worker path [mode:%s]', mode);

			const effectTrack = await OffscreenEffectTrack.create({
				inputTrack,
				mode,
				blurPercentage,
				imageUrl
			});

			this.effectTracks.set(effectTrack.outputTrack.id, effectTrack);

			return effectTrack.outputTrack;
		}
		catch (error)
		{
			logger.warn('_tryOffscreenEffect() failed, will use main-thread fallback: %o', error);

			return null;
		}
	}

	/**
	 * Ensure the TFLite WASM module is ready and the segmentation model is
	 * loaded into its HEAP exactly once. Safe to call from multiple apply*
	 * paths; callers must still serialize live inference via pause().
	 */
	async ensureModelLoaded()
	{
		if (this._ensureModelPromise)
		{
			return this._ensureModelPromise;
		}

		this._ensureModelPromise = (async () =>
		{
			const MLBackend = await this.createMLBackend();

			if (!this.model)
			{
				this.model = await this.createModel();
			}

			if (!this.modelLoadedOntoBackend)
			{
				MLBackend.HEAPU8.set(
					new Uint8Array(this.model),
					MLBackend._getModelBufferMemoryOffset()
				);
				MLBackend._loadModel(this.model.byteLength);
				this.modelLoadedOntoBackend = true;
				logger.debug('ensureModelLoaded() model loaded onto WASM backend');
			}

			return MLBackend;
		})();

		try
		{
			return await this._ensureModelPromise;
		}
		catch (error)
		{
			this._ensureModelPromise = null;
			throw error;
		}
	}

	async createMLBackend()
	{
		// Reuse the already-instantiated backend. Sharing a single WASM
		// instance/HEAP avoids a costly reload on every switch; exclusivity
		// is enforced by pausing the previous effect before starting a new one.
		if (this.mlBackend)
		{
			return this.mlBackend;
		}

		let MLBackend;
		const LOAD_BACKEND_TIMEOUT = 10000;

		try
		{
			MLBackend = await timeoutPromise(this.loadTFLiteModule('tflite-simd.js'), LOAD_BACKEND_TIMEOUT);
			if (!MLBackend) throw new Error('No ML Backend');
		}
		catch (error)
		{
			logger.error(error);
		}

		if (!MLBackend)
		{
			try
			{
				MLBackend = await timeoutPromise(this.loadTFLiteModule('tflite.js'), LOAD_BACKEND_TIMEOUT);
				if (!MLBackend) throw new Error('No ML Backend');
			}
			catch (error)
			{
				logger.error(error);
			}
		}
		// eslint-disable-next-line no-console
		// console.log('response', MLBackend);

		if (!MLBackend)
		{
			throw new Error('Could not create ML Backend');
		}

		this.mlBackend = MLBackend;

		return MLBackend;
	}

	async loadTFLiteModule(filename)
	{
		return new Promise((resolve, reject) =>
		{
			// Dynamically load the TFLite module from the public folder.
			// The glue scripts live under /tflite/ (also preloaded in
			// index.html); loading from the root 404s to index.html and throws
			// an "Unexpected token '<'" parse error.
			const script = document.createElement('script');

			script.src = `/tflite/${filename}`;
			script.async = true;

			script.onload = async () =>
			{
				try
				{
					const module = filename.includes('simd')
						? await createTFLiteSIMDModule()
						: await createTFLiteModule();

					resolve(module);
				}
				catch (error)
				{
					reject(new Error(`Failed to initialize ${filename}: ${error.message}`));
				}
			};

			script.onerror = () =>
			{
				reject(new Error(`Failed to load script: ${filename}`));
			};

			document.body.appendChild(script);
		});
	}

	async createModel()
	{
		const response = await fetch(modelConfig.path);

		if (!response.ok) throw new Error('Could not load model');

		// // eslint-disable-next-line no-console
		// console.log('response', response.arrayBuffer());

		return await response.arrayBuffer();
	}
}